/**
 * Styles for the Simple home page, scoped under `.ccs` so nothing leaks into (or out of) the app.
 *
 * Three skins on the same page, chosen by `data-skin`:
 *   cream  the proposal: warm paper, ink, one cherry accent
 *   red    the hero and nav in cherry red, the sections below on paper
 *   dark   the phone app's colours: near-black, cherry accent
 */
export const SIMPLE_CSS = `
.ccs{
  --bg:#f7f2ee;--card:#fff;--ink:#1d1418;--mut:#6b5f64;--line:#e6dcd6;--soft:#fde3e8;
  --cherry:#e61d50;--cherry2:#ff2e63;--cherry-ink:#fff;
  --hero-bg:var(--bg);--hero-ink:var(--ink);--hero-mut:var(--mut);--hero-line:#d9c9c2;
  --bar-bg:var(--card);--bar-ink:var(--ink);--bar-mut:var(--mut);
  --up-bg:var(--cherry);--up-ink:#fff;--up-hover:#cc1545;
  --nav-bg:rgba(247,242,238,.92);--nav-ink:var(--ink);--nav-mut:var(--mut);
  --band:#fff;--shadow:0 18px 50px rgba(29,20,24,.08);--row-on:#fff4f6;--mark:#ffe08a;--mark-ink:#1d1418;
  background:var(--bg);color:var(--ink);font:16px/1.5 Outfit,-apple-system,system-ui,sans-serif;min-height:100vh;overflow-x:clip;
}
.ccs[data-skin="red"]{
  --hero-bg:linear-gradient(180deg,#e61d50 0%,#c8103f 100%);--hero-ink:#fff;--hero-mut:rgba(255,255,255,.86);--hero-line:rgba(255,255,255,.55);
  --up-bg:#1d1418;--up-ink:#fff;--up-hover:#000;
  --nav-bg:rgba(230,29,80,.96);--nav-ink:#fff;--nav-mut:rgba(255,255,255,.85);
}
.ccs[data-skin="dark"]{
  --bg:#0e0b0d;--card:#17131a;--ink:#f6eff1;--mut:#a99ca2;--line:#2b2328;--soft:rgba(230,29,80,.16);
  --cherry:#ff2e63;--cherry2:#ff5a85;
  --hero-line:#3a3036;--bar-bg:#17131a;
  --up-bg:#e61d50;--up-hover:#ff2e63;
  --nav-bg:rgba(14,11,13,.9);
  --band:#120f13;--shadow:0 18px 50px rgba(0,0,0,.45);--row-on:rgba(230,29,80,.12);--mark:#ff2e63;--mark-ink:#fff;
}
.ccs *{box-sizing:border-box}
.ccs a{color:inherit;text-decoration:none}
.ccs button{font:inherit;cursor:pointer;border:0;background:none;color:inherit;padding:0}
.ccs .wrap{max-width:1100px;margin:0 auto;padding:0 24px}

/* nav */
.ccs nav.top{position:sticky;top:0;z-index:20;background:var(--nav-bg);backdrop-filter:blur(8px);-webkit-backdrop-filter:blur(8px);color:var(--nav-ink)}
.ccs .nav-in{display:flex;align-items:center;gap:26px;height:70px;max-width:1240px;margin:0 auto;padding:0 24px}
.ccs .brand{display:flex;align-items:center;gap:10px;font-weight:800;font-size:22px;letter-spacing:-.01em;white-space:nowrap}
.ccs .brand img{height:32px;width:auto;display:block}
.ccs .brand b{color:var(--cherry)}
.ccs[data-skin="red"] .brand b{color:#fff;opacity:.85}
.ccs[data-skin="red"] .brand img{filter:brightness(0) invert(1)}
.ccs .links{display:flex;gap:24px;margin-left:10px;color:var(--nav-mut);font-weight:500;font-size:15px;white-space:nowrap}
.ccs .links button:hover{color:var(--nav-ink)}
.ccs .right{margin-left:auto;display:flex;gap:10px;align-items:center}
.ccs .lib{display:flex;align-items:center;gap:8px;border:1.5px solid currentColor;border-radius:999px;padding:8px 18px;font-weight:600;white-space:nowrap}
.ccs .lib svg{width:18px;height:18px}
.ccs .signin{display:flex;align-items:center;gap:8px;font-weight:600;color:var(--nav-mut);padding:8px 6px;white-space:nowrap}
.ccs .signin:hover{color:var(--nav-ink)}
.ccs .who{max-width:180px;overflow:hidden;text-overflow:ellipsis}
.ccs .getapp{display:none;background:var(--cherry);color:#fff;border-radius:999px;padding:8px 14px;font-weight:700;font-size:14px;white-space:nowrap}
.ccs[data-skin="red"] .getapp{background:#1d1418}
@media (max-width:1060px){.ccs .links{display:none}}
@media (max-width:640px){
  .ccs .nav-in{gap:10px;padding:0 16px;height:62px}
  .ccs .brand{font-size:19px}.ccs .brand img{height:28px}
  .ccs .lib{padding:7px 12px;font-size:14px}.ccs .lib .lib-word{display:none}
  .ccs .signin .signin-word{display:none}
  .ccs .getapp{display:inline-block}
}

/* hero */
.ccs .hero{background:var(--hero-bg);color:var(--hero-ink)}
.ccs .hero-in{max-width:980px;margin:0 auto;padding:76px 24px 44px;text-align:center}
.ccs .hero h1{font-size:68px;line-height:1.04;letter-spacing:-.025em;margin:0;font-weight:800}
.ccs .hero h1 em{font-style:normal;color:var(--cherry);white-space:nowrap}
.ccs[data-skin="red"] .hero h1 em{color:#fff;text-decoration:underline;text-decoration-thickness:6px;text-underline-offset:10px;text-decoration-color:rgba(255,255,255,.55)}
.ccs .hero .sub{font-size:22px;color:var(--hero-mut);margin:22px auto 34px;max-width:760px}
/* the product as three numbered steps under the headline, like a playlist */
.ccs .play-steps{list-style:none;display:flex;justify-content:center;flex-wrap:wrap;gap:10px 22px;margin:24px auto 30px;padding:0;font-size:19px;color:var(--hero-mut)}
.ccs .play-steps li{display:flex;align-items:center;gap:9px}
.ccs .play-steps b{width:28px;height:28px;border-radius:50%;display:grid;place-items:center;background:var(--soft);color:var(--cherry);font-size:14px;font-weight:800;flex:none}
.ccs[data-skin="red"] .play-steps b{background:rgba(255,255,255,.2);color:#fff}
.ccs .bar{display:flex;align-items:center;gap:16px;max-width:760px;margin:0 auto;background:var(--bar-bg);color:var(--bar-ink);border:2px dashed var(--hero-line);border-radius:999px;padding:12px 12px 12px 30px;box-shadow:var(--shadow);transition:border-color .2s,box-shadow .2s,transform .2s;cursor:pointer}
.ccs[data-skin="red"] .bar{border-color:transparent;box-shadow:0 18px 50px rgba(80,0,20,.35)}
.ccs .bar.hot{border-color:var(--cherry);box-shadow:0 18px 60px rgba(230,29,80,.22);transform:scale(1.01)}
.ccs .bar .ic{width:30px;height:30px;color:var(--cherry);flex:none}
.ccs .bar .drop{flex:1;text-align:left;font-size:19px;color:var(--bar-mut)}
.ccs .bar .drop b{color:var(--bar-ink);font-weight:600}
.ccs .up{background:var(--up-bg);color:var(--up-ink);font-weight:700;font-size:19px;border-radius:999px;padding:16px 30px;white-space:nowrap;box-shadow:0 8px 22px rgba(230,29,80,.3);transition:background .2s}
.ccs .up:hover{background:var(--up-hover)}
.ccs .under{display:flex;justify-content:center;align-items:center;gap:18px;flex-wrap:wrap;margin:18px auto 0;max-width:760px;font-size:15px;color:var(--hero-mut)}
.ccs .under .linkbtn{display:inline-flex;align-items:center;gap:6px;font-weight:600;color:var(--hero-ink)}
.ccs .under .linkbtn svg{width:16px;height:16px}
.ccs .under select{font:inherit;font-size:15px;color:var(--hero-ink);background:transparent;border:1px solid var(--hero-line);border-radius:999px;padding:4px 10px;cursor:pointer}
.ccs .under select option{color:#1d1418}
.ccs .paste{display:flex;gap:10px;max-width:760px;margin:14px auto 0}
.ccs .paste input{flex:1;min-width:0;font:inherit;font-size:16px;border:1.5px solid var(--hero-line);background:var(--bar-bg);color:var(--bar-ink);border-radius:999px;padding:12px 20px;outline:none}
.ccs .paste input:focus{border-color:var(--cherry)}
.ccs .paste button{background:var(--up-bg);color:var(--up-ink);font-weight:700;border-radius:999px;padding:12px 22px}
.ccs .paste button:disabled{opacity:.6;cursor:default}
.ccs .paste-msg{margin:8px auto 0;font-size:14px;color:var(--hero-mut);min-height:20px;text-align:center}
.ccs .paste-msg .src-mark{width:16px;height:16px;display:inline-block;vertical-align:-3px;margin-right:4px}
.ccs .paste-msg.bad{color:var(--hero-ink);font-weight:600}
.ccs .ticks{display:flex;justify-content:center;gap:26px;flex-wrap:wrap;margin:22px 0 0;color:var(--hero-mut);font-size:15px}
.ccs .ticks span::before{content:"✓ ";color:var(--cherry);font-weight:800}
.ccs[data-skin="red"] .ticks span::before{color:#fff}
.ccs .back{margin-top:14px;font-size:16px;color:var(--hero-mut)}
.ccs .back button{color:var(--cherry);font-weight:700}
.ccs[data-skin="red"] .back button{color:#fff;text-decoration:underline}
@media (max-width:760px){
  .ccs .hero-in{padding:44px 16px 44px}
  .ccs .hero h1{font-size:40px}
  .ccs .hero .sub{font-size:18px;margin:16px auto 24px}
  .ccs .bar{flex-direction:column;border-radius:24px;padding:18px;gap:10px}
  .ccs .bar .drop{display:none}
  .ccs .bar .ic{display:none}
  .ccs .up{width:100%;padding:16px}
  .ccs .ticks{gap:6px 16px;font-size:14px}
  .ccs .paste{flex-direction:column}
}

/* upload progress (replaces the bar) */
.ccs .prog{max-width:760px;margin:0 auto;background:var(--bar-bg);color:var(--bar-ink);border-radius:28px;padding:22px 28px;box-shadow:var(--shadow);text-align:left}
.ccs .prog .row{display:flex;align-items:center;gap:14px;font-weight:600;font-size:18px}
.ccs .prog .name{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.ccs .prog .pct{margin-left:auto;color:var(--cherry);font-variant-numeric:tabular-nums}
.ccs .track{height:10px;background:var(--line);border-radius:99px;margin:14px 0 10px;overflow:hidden}
.ccs .track i{display:block;height:100%;background:linear-gradient(90deg,var(--cherry),var(--cherry2));border-radius:99px;transition:width .3s}
.ccs .prog p{margin:0;color:var(--bar-mut);font-size:15px}

/* the clip list under the hero, labelled as an example so nobody takes it for their library */
.ccs .shot{max-width:1180px;margin:48px auto 0;padding:0 24px}
.ccs .editor{border-radius:18px}
.ccs .editor-wait{height:676px;border-radius:18px;background:#0b0a0f}
.ccs .shot-head{text-align:center;margin:0 auto 22px;max-width:720px}
.ccs .shot-head .tag{display:inline-block;background:var(--soft);color:var(--cherry);font-weight:800;font-size:13px;letter-spacing:.08em;text-transform:uppercase;border-radius:999px;padding:5px 14px;margin-bottom:12px}
.ccs .shot-head h2{font-size:34px;margin:0 0 8px}
.ccs .shot-head .lede{margin:0 auto}
/* three ways to cut + works with */
.ccs .works{display:flex;flex-wrap:wrap;justify-content:center;align-items:center;gap:10px 12px;margin:18px auto 30px}
.ccs .works-label{display:inline-flex;align-items:center;gap:8px;font-weight:700;color:var(--mut);margin-right:4px}
.ccs .works-label img{height:26px;width:auto}
.ccs .partner{display:inline-flex;align-items:center;gap:9px;background:var(--card);border:1px solid var(--line);border-radius:999px;padding:7px 16px 7px 9px;font-weight:700;font-size:15px}
.ccs .ways{display:grid;grid-template-columns:repeat(3,1fr);gap:20px;text-align:left}
.ccs .way{position:relative;background:var(--card);border:1px solid var(--line);border-radius:22px;padding:26px 26px 22px}
.ccs .way-icons{display:flex;align-items:center;gap:10px;height:40px;margin-bottom:14px}
.ccs .way-icons .way-cc{height:32px;width:auto}
.ccs .way-n{position:absolute;top:22px;right:24px;font-weight:800;color:var(--line);font-size:30px;line-height:1}
.ccs .way h3{margin:0 0 6px;font-size:22px}
.ccs .way p{margin:0 0 14px;color:var(--mut)}
.ccs .pr-mark{display:inline-grid;place-items:center;border-radius:22%;background:#00005b;color:#9999ff;font-style:normal;font-weight:800;border:1.5px solid #9999ff;line-height:1;flex:none}
@media (max-width:900px){.ccs .ways{grid-template-columns:1fr}}

/* the Upload bar on another page (W / Y / Z) */
.ccs.inline{min-height:0;background:transparent;color:inherit;margin-top:22px;max-width:640px}
.ccs.inline .inline-label{margin:0 0 10px;font-size:15px;font-weight:600;color:var(--hero-mut)}
.ccs.inline .bar{margin:0;padding:8px 8px 8px 22px;gap:12px}
.ccs.inline .bar .drop{font-size:16px}
.ccs.inline .up{font-size:16px;padding:12px 22px}
.ccs.inline .under{justify-content:flex-start;margin:12px 0 0}
.ccs.inline .paste,.ccs.inline .prog{margin:12px 0 0}

/* sections */
.ccs section.s{padding:96px 0 0}
.ccs .eyebrow{display:inline-block;font-size:13px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:var(--cherry);margin-bottom:12px}
.ccs h2{font-size:44px;line-height:1.08;letter-spacing:-.02em;margin:0 0 14px;font-weight:800}
.ccs .lede{font-size:19px;color:var(--mut);margin:0;max-width:560px}
.ccs .center{text-align:center}
.ccs .center .lede{margin:0 auto}
.ccs .steps{display:grid;grid-template-columns:repeat(3,1fr);gap:20px;margin-top:32px}
.ccs .step{background:var(--card);border-radius:20px;padding:26px;border:1px solid var(--line)}
.ccs .step .n{width:34px;height:34px;border-radius:50%;background:var(--soft);color:var(--cherry);font-weight:800;display:grid;place-items:center}
.ccs .step h3{margin:14px 0 6px;font-size:22px}
.ccs .step p{margin:0;color:var(--mut)}
.ccs .split{display:grid;grid-template-columns:1.05fr .95fr;gap:56px;align-items:center}
.ccs .split.flip{grid-template-columns:.95fr 1.05fr}
.ccs .panel{background:var(--band);border:1px solid var(--line);border-radius:28px;padding:48px}
.ccs .btns{display:flex;flex-wrap:wrap;gap:12px;align-items:center;margin-top:26px}
.ccs .btn{display:inline-flex;align-items:center;gap:8px;border-radius:999px;padding:13px 24px;font-weight:700;font-size:16px;white-space:nowrap}
.ccs .btn.solid{background:var(--cherry);color:#fff}
.ccs .btn.solid:hover{background:#cc1545}
.ccs .btn.ghost{border:1.5px solid var(--line);color:var(--ink)}
.ccs .btn.ghost:hover{border-color:var(--ink)}
.ccs .textlink{color:var(--cherry);font-weight:700}
@media (max-width:900px){
  .ccs section.s{padding:64px 0 0}
  .ccs h2{font-size:32px}
  .ccs .lede{font-size:17px}
  .ccs .steps{grid-template-columns:1fr}
  .ccs .split,.ccs .split.flip{grid-template-columns:1fr;gap:32px}
  .ccs .panel{padding:28px 20px;border-radius:22px}
  .ccs .split.flip .media{order:2}
}

/* phone app */
.ccs .badges{display:flex;flex-wrap:wrap;gap:14px;align-items:center;margin-top:26px}
.ccs .badges a{display:block}
.ccs .badges img.as{height:48px;display:block}
.ccs .badges img.gp{height:70px;margin:-11px -10px;display:block}
.ccs .qr{display:flex;align-items:center;gap:14px;margin-top:22px;color:var(--mut);font-size:15px;max-width:330px}
.ccs .qr svg{width:92px;height:92px;flex:none;border-radius:12px;box-shadow:0 0 0 1px var(--line)}
@media (max-width:900px){.ccs .qr{display:none}}
.ccs .phones{display:flex;justify-content:center;gap:22px}
.ccs .phone{width:236px;aspect-ratio:9/19.5;border-radius:38px;background:#0b0809;padding:9px;box-shadow:0 30px 70px rgba(29,20,24,.25),inset 0 0 0 2px #2a2326;flex:none}
.ccs .phone video,.ccs .phone img{width:100%;height:100%;object-fit:cover;border-radius:30px;display:block;background:#000}
@media (max-width:640px){.ccs .phone{width:62%}}

/* AI */
.ccs .film{position:relative;border-radius:22px;overflow:hidden;background:#000;box-shadow:0 30px 80px rgba(29,20,24,.2);aspect-ratio:16/9}
.ccs .film video,.ccs .film img{width:100%;height:100%;display:block;object-fit:cover}
.ccs .film .play{position:absolute;inset:0;display:grid;place-items:center;background:linear-gradient(transparent 40%,rgba(0,0,0,.45))}
.ccs .film .play span{display:inline-flex;align-items:center;gap:10px;background:#fff;color:#1d1418;font-weight:700;border-radius:999px;padding:14px 24px;box-shadow:0 10px 30px rgba(0,0,0,.3)}
.ccs .film .play span::before{content:"";width:0;height:0;border-left:13px solid var(--cherry);border-top:8px solid transparent;border-bottom:8px solid transparent}
.ccs .asksteps{list-style:none;margin:24px 0 0;padding:0;display:grid;gap:12px}
.ccs .asksteps li{display:flex;align-items:center;gap:12px;font-size:17px;font-weight:600}
.ccs .asksteps li b{width:30px;height:30px;border-radius:50%;background:var(--soft);color:var(--cherry);display:grid;place-items:center;font-size:14px;flex:none}
.ccs .say{margin-top:22px;padding:14px 18px;border-radius:16px;background:var(--card);border:1px solid var(--line);font-size:17px}
.ccs .logos{display:flex;flex-wrap:wrap;gap:10px;margin-bottom:16px}
.ccs .logos span{display:inline-flex;align-items:center;gap:10px;border:1px solid var(--line);border-radius:999px;padding:8px 18px 8px 12px;font-weight:700;font-size:17px;background:var(--card);color:var(--ink)}
.ccs .logos svg{width:26px;height:26px;display:block}

/* Premiere */
/* Premiere: the words in one row, the real panel big underneath */
.ccs .pr-panel{display:grid;gap:30px}
.ccs .pr-words{display:flex;justify-content:space-between;align-items:flex-end;gap:32px;flex-wrap:wrap}
.ccs .pr-words .lede{max-width:600px}
.ccs .pr-words .btns{margin-top:0}
.ccs .pr-shot{border-radius:18px;overflow:hidden;box-shadow:0 30px 80px rgba(29,20,24,.2);border:1px solid var(--line);background:#1d1d1d}
.ccs .pr-shot img{width:100%;display:block}
.ccs .pr-tag{display:inline-flex;align-items:center;gap:12px;margin-bottom:14px}

/* Claude and ChatGPT, wired to CherryClipz */
.ccs .linked{display:flex;align-items:center;flex-wrap:wrap;gap:0;margin-bottom:22px}
.ccs .linked .tile{display:inline-flex;align-items:center;gap:10px;background:var(--card);border:1px solid var(--line);border-radius:16px;padding:10px 16px 10px 12px;font-weight:800;font-size:18px;box-shadow:var(--shadow)}
.ccs .linked .tile.cc img{height:30px;width:auto}
.ccs .linked .wire{width:34px;height:0;border-top:2.5px dashed var(--cherry);opacity:.7}
@media (max-width:640px){.ccs .linked .tile{font-size:15px;padding:8px 12px 8px 9px}.ccs .linked .wire{width:16px}}

/* demos */
.ccs .demos{display:grid;grid-template-columns:repeat(3,1fr);gap:20px;margin-top:32px}
.ccs .demo{text-align:left;background:var(--card);border:1px solid var(--line);border-radius:20px;overflow:hidden;transition:transform .2s,box-shadow .2s}
.ccs .demo:hover{transform:translateY(-3px);box-shadow:var(--shadow)}
.ccs .demo .pic{position:relative;aspect-ratio:16/9;background:#000 center 30%/cover}
.ccs .demo .pic::after{content:"";position:absolute;left:50%;top:50%;width:58px;height:58px;margin:-29px 0 0 -29px;border-radius:50%;background:rgba(255,255,255,.95) url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M9 6l10 6-10 6z' fill='%23e61d50'/%3E%3C/svg%3E") center/26px no-repeat;box-shadow:0 8px 24px rgba(0,0,0,.3)}
.ccs .demo .b{padding:16px 18px}
.ccs .demo h3{margin:0 0 4px;font-size:19px}
.ccs .demo p{margin:0;color:var(--mut);font-size:15px}
@media (max-width:900px){.ccs .demos{grid-template-columns:1fr}}
.ccs .modal{position:fixed;inset:0;z-index:60;background:rgba(10,6,8,.82);display:grid;place-items:center;padding:24px}
.ccs .modal .box{position:relative;width:min(1100px,100%);aspect-ratio:16/9;background:#000;border-radius:16px;overflow:hidden}
.ccs .modal .box.tall{width:auto;height:min(86vh,900px);aspect-ratio:9/16}
.ccs .modal video,.ccs .modal iframe{width:100%;height:100%;border:0;display:block}
.ccs .modal .x{position:absolute;top:-6px;right:-6px;transform:translate(0,-100%);color:#fff;font-size:15px;font-weight:700;padding:8px 4px}

/* pricing + footer */
.ccs .price{margin-top:96px;background:var(--cherry);color:#fff;border-radius:28px;padding:44px 48px;display:flex;align-items:center;justify-content:space-between;gap:24px;flex-wrap:wrap}
.ccs .price h2{margin:0 0 4px;font-size:36px}
.ccs .price p{margin:0;font-size:18px;opacity:.9}
.ccs .price .btn{background:#fff;color:#1d1418}
.ccs[data-skin="dark"] .price{background:linear-gradient(135deg,#e61d50,#a30d35)}
@media (max-width:640px){.ccs .price{padding:30px 22px;margin-top:64px}.ccs .price h2{font-size:28px}}
.ccs footer{margin:72px 0 0;padding:30px 0 40px;border-top:1px solid var(--line);color:var(--mut);font-size:14px}
.ccs footer .wrap{display:flex;flex-wrap:wrap;gap:12px 24px;align-items:center}
.ccs footer button:hover{color:var(--ink)}
.ccs footer .tm{flex-basis:100%;margin:0;font-size:12px;line-height:1.5}
`;
